import {SelectProps} from '@amzn/awsui-components-react-v3';
import {OptionDefinition} from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';

import {CommonConstants} from './BackendEntityConstants';
import {
  RFQ_DCDIVEST_LINEITEM_CLASS,
  RFQ_DCDIVEST_REQUEST_CLASS,
  RFQ_DCDIVEST_RESPONSE_CLASS,
  RFQ_DCSP_DEFAULT_LINEITEM_CLASS,
  RFQ_DCSP_GLOBAL_COMMISSIONING_BID_LINEITEM_CLASS,
  RFQ_DCSP_GLOBAL_CONTROLS_BID_LINEITEM_CLASS,
  RFQ_DCSP_GLOBAL_DE_LINEITEM_CLASS,
  RFQ_DCSP_GLOBAL_GC_BID_LINEITEM_CLASS,
  RFQ_DCSP_REQUEST_CLASS,
  RFQ_DCSP_RESPONSE_CLASS,
  RFQ_IPS_DETAIL_LINEITEM_CLASS,
  RFQ_IPS_EAM_LINEITEM_CLASS,
  RFQ_IPS_LINEITEM_CLASS,
  RFQ_IPS_NON_EAM_LINEITEM_CLASS,
  RFQ_IPS_REQUEST_CLASS,
  RFQ_IPS_RESPONSE_CLASS,
  RFQ_IPS_SECURITY_GUARD_LINEITEM_CLASS,
  RFQ_LINEITEM_CLASS,
  RFQ_REQUEST_CLASS,
  RFQ_RESPONSE_CLASS
} from './RFQClassDefinitionsConstants';

// BusinessId Constants
export const ADMIN = 'ADMIN';
export const DCDIVEST = 'DCDIVEST';
export const PAC = 'PAC';
export const IPS = 'IPS';
export const DCSP = 'DCSP';

export const DEFAULT_BUSINESS_ID = 'PAC';

// RFX types
export const RFXTypes = {
  RFQ : 'RFQ',
  RFI : 'RFI'
}

// Workflow Constants
export const DCSP_LLE = 'DCSP - Long Lead Equipment';

// Quote Template Type Constants
export const IPSEAMTemplate = 'EAM RFQ Template';
export const IPSEAMDetailTemplate = 'EAM Detail Template';
export const IPSNonEAMTemplate = 'Non-EAM Template';
export const IPSSecurityGuardTemplate = 'Security Guard Template';
export const DCSPGlobalBidTemplate = 'Global Bid Template'; // This is the default template for DCSP
export const DCSPGlobalCommissioningBidTemplate = 'Global Commissioning Bid Template';
export const DCSPGlobalDEBidTemplate = 'Global DE Bid Template';
export const DCSPGlobalGCBidTemplate = 'Global GC Bid Template';
export const DCSPGlobalControlsBidTemplate = 'Global Controls Bid Template';

export const ALL_BUSINESS_ID_LIST = [PAC, DCDIVEST, IPS, DCSP];

const SKIP_OFFER_GENERATION_BUSINESS_LIST = [DCDIVEST, IPS, DCSP];

const NOT_SKIP_OFFER_GENERATION_BUSINESS_LIST = [PAC];

export const UNSET_REGION_OPTION:  SelectProps.Option = { value: '1', label: CommonConstants.UNSET_REGION_VALUE};

export function shouldSkipOfferGeneration(businessId: string) {
  return SKIP_OFFER_GENERATION_BUSINESS_LIST.includes(businessId);
}

export function shouldNotSkipOfferGeneration(businessId: string | null | undefined): boolean {
  return !!businessId && NOT_SKIP_OFFER_GENERATION_BUSINESS_LIST.includes(businessId);
}

export function shouldAllowMultiAward(businessId: string | null | undefined): boolean {
  switch(businessId) {
    case IPS: return true;
    default: break;
  }
  return false;
}

/**
 * Determine whether SMART should display the currency for the RFX.
 * DCSP RFX has all lineitems with the same currency as the request
 * Other business groups have different currencies for different lineitems.
 * @param businessId
 */
export function shouldDisplayCurrency(businessId: string | null | undefined): boolean {
  switch (businessId) {
    case DCSP:
      return true;
    default:
      break;
  }
  return false;
}

/**
 * Determines if a quote template type should be present based on the business ID and workflow.
 *
 * @param businessId - The business ID to check. Can be a string, null, or undefined.
 * @param workflow - The workflow to check.
 * @returns A boolean indicating whether the quote template type should be present.
 */

export function shouldHaveQuoteTemplateType(
    businessId: string | null | undefined,
    workflow: string
  ): boolean {
  switch(businessId) {
    case IPS:
      return workflow !== DCSP_LLE;
    case DCSP:
      return false;
    default: break;
  }
  return false;
}

export function isValidBusinessId(businessId: string | null | undefined): boolean {
  return businessId === PAC ||
    businessId === DCDIVEST ||
    businessId === IPS ||
    businessId === DCSP;
}

/**
 * Get the RFX Request Class ID based on the business ID and workflow.
 *
 * @param businessId - The business identifier.
 * @param workflow - The workflow identifier.
 * @returns The corresponding RFX Request Class ID.
 */
export const getRFXRequestClassId = (
    businessId: string,
    workflow: string
): string => {
  switch(businessId) {
    case DCDIVEST: return RFQ_DCDIVEST_REQUEST_CLASS;
    case IPS: return RFQ_IPS_REQUEST_CLASS;
    case DCSP:
      if ( workflow === DCSP_LLE ) {
        return RFQ_REQUEST_CLASS;
      }
      return RFQ_DCSP_REQUEST_CLASS;
    case PAC:
    default:
      return RFQ_REQUEST_CLASS;
  }
}

/**
 * Get the RFX Response Class ID based on the business ID and workflow.
 *
 * @param businessId - The business identifier.
 * @param workflow - The workflow identifier.
 * @returns The corresponding RFX Response Class ID.
 */
export const getRFXResponseClassId = (
    businessId: string,
    workflow: string
  ): string => {
  switch(businessId) {
    case DCDIVEST: return RFQ_DCDIVEST_RESPONSE_CLASS;
    case IPS: return RFQ_IPS_RESPONSE_CLASS;
    case DCSP:
      if (workflow === DCSP_LLE) {
        return RFQ_RESPONSE_CLASS;
      }
      return RFQ_DCSP_RESPONSE_CLASS;
    case PAC:
    default:
      return RFQ_RESPONSE_CLASS;
  }
}

/**
 * Get the RFX Line Item Class ID based on the business ID, workflow, and template type.
 *
 * @param businessId - The business identifier.
 * @param workflow - The workflow identifier.
 * @param templateType - Optional template type for IPS and DCSP businesses.
 * @returns The corresponding RFX Line Item Class ID.
 */
export const getRFXLineItemClassId = (
    businessId: string,
    workflow: string,
    templateType?: string | undefined): string => {
  switch(businessId) {
    case DCDIVEST: return RFQ_DCDIVEST_LINEITEM_CLASS;
    case IPS: return getRFXLineItemClassIdForIPSTemplateType(templateType);
    case DCSP:
      if (workflow === DCSP_LLE) {
        return RFQ_LINEITEM_CLASS;
      }
      return getRFXLineItemClassIdForDCSPTemplateType(templateType);
    case PAC:
    default:
      return RFQ_LINEITEM_CLASS;
  }
}

/**
 * Determines the RFX line item class ID based on the IPS template type.
 *
 * @param templateType - The IPS template type.
 * @returns The corresponding RFX line item class ID.
 */
export const getRFXLineItemClassIdForIPSTemplateType = (templateType: string | undefined): string => {
  switch(templateType) {
    case IPSEAMDetailTemplate: return RFQ_IPS_DETAIL_LINEITEM_CLASS;
    case IPSNonEAMTemplate: return RFQ_IPS_NON_EAM_LINEITEM_CLASS;
    case IPSSecurityGuardTemplate: return RFQ_IPS_SECURITY_GUARD_LINEITEM_CLASS;
    case IPSEAMTemplate: return RFQ_IPS_EAM_LINEITEM_CLASS;
    default: break;
  }
  return RFQ_IPS_LINEITEM_CLASS;
}

/**
 * Determines the RFX Line Item Class ID for DCSP based on the template type.
 *
 * @param templateType - The type of DCSP template. If undefined, returns the default class ID.
 * @returns The appropriate RFX Line Item Class ID for the given DCSP template type.
 */
export const getRFXLineItemClassIdForDCSPTemplateType = (templateType: string | undefined): string => {
  switch(templateType) {
    case DCSPGlobalBidTemplate: return RFQ_DCSP_DEFAULT_LINEITEM_CLASS;
    case DCSPGlobalCommissioningBidTemplate: return RFQ_DCSP_GLOBAL_COMMISSIONING_BID_LINEITEM_CLASS;
    case DCSPGlobalDEBidTemplate: return RFQ_DCSP_GLOBAL_DE_LINEITEM_CLASS;
    case DCSPGlobalGCBidTemplate: return RFQ_DCSP_GLOBAL_GC_BID_LINEITEM_CLASS;
    case DCSPGlobalControlsBidTemplate: return RFQ_DCSP_GLOBAL_CONTROLS_BID_LINEITEM_CLASS;
    default:
      return RFQ_DCSP_DEFAULT_LINEITEM_CLASS;
  }
}


export const getCategoryOptionsForIPSTemplateType = (
    quoteTemplateType: string | undefined,
    categoryOptions: OptionDefinition[][]):OptionDefinition[]  => {
  switch(quoteTemplateType) {
    case IPSEAMTemplate:
    case IPSEAMDetailTemplate: return categoryOptions[0];
    case IPSNonEAMTemplate: return categoryOptions[1];
    case IPSSecurityGuardTemplate: return categoryOptions[2];
    default: break;
  }
  return categoryOptions[3];
}